import { useCallback, useEffect, useMemo, useState } from "react";
import { Group, Layer, Line, Rect, Stage, Text } from "react-konva";
import { Polyomino } from "../lib/polyomino/polyomino";

// 五十音表: 11列 x 5行 (右上から左下に縦書き)
// 列: ん わ ら や ま は な た さ か あ
//     ・ ・ り ・ み ひ に ち し き い
//     ・ ・ る ゆ む ふ ぬ つ す く う
//     ・ ・ れ ・ め へ ね て せ け え
//     ・ を ろ よ も ほ の と そ こ お

const GOJUON_BOARD: string[][] = [
  // 行0 (上から)
  ["ん", "わ", "ら", "や", "ま", "は", "な", "た", "さ", "か", "あ"],
  ["", "", "り", "", "み", "ひ", "に", "ち", "し", "き", "い"],
  ["", "", "る", "ゆ", "む", "ふ", "ぬ", "つ", "す", "く", "う"],
  ["", "", "れ", "", "め", "へ", "ね", "て", "せ", "け", "え"],
  ["", "を", "ろ", "よ", "も", "ほ", "の", "と", "そ", "こ", "お"],
];

// 有効なセルのインデックスを計算
const getValidCells = (): { x: number; y: number; char: string }[] => {
  const cells: { x: number; y: number; char: string }[] = [];
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 11; x++) {
      if (GOJUON_BOARD[y][x] !== "") {
        cells.push({ x, y, char: GOJUON_BOARD[y][x] });
      }
    }
  }
  return cells;
};

const VALID_CELLS = getValidCells();
// 有効セル数: 46文字
// 46 - 5(空けるマス) = 41セルをカバーする必要がある
const FINAL_PIECES = [
  // 6セル x 1 = 6
  new Polyomino.Piece(3, 2, [
    true, true, true,
    true, true, true,
  ]),
  // 5セル x 7 = 35
  new Polyomino.Piece(4, 2, [
    true, false, false, false,
    true, true, true, true,
  ]),
  new Polyomino.Piece(3, 2, [
    true, false, true,
    true, true, true,
  ]),
  new Polyomino.Piece(2, 3, [
    true, true,
    true, true,
    true, false,
  ]),
  new Polyomino.Piece(3, 3, [
    true, false, false,
    true, false, false,
    true, true, true,
  ]),
  new Polyomino.Piece(4, 2, [
    false, true, false, false,
    true, true, true, true,
  ]),
  new Polyomino.Piece(4, 2, [
    true, true, false, false,
    false, true, true, true,
  ]),
  // S型ペントミノ (十字型の代わり)
  new Polyomino.Piece(3, 3, [
    false, true, true,
    false, true, false,
    true, true, false,
  ]),
];
// 合計: 6 + 35 = 41セル

// 配色設定
const COLORS = {
  // 背景
  background: "#C8D8C8",  // 薄いミントグリーン
  // ボード
  boardBg: "#FFFCF5",
  boardBorder: "#AAA",
  // グリッド
  gridLine: "#CCC",
  // 無効セル（黒マス）
  blackCell: "#4A4A4A",
  // 文字
  ink: "#333",
  // 空きマス（答えの文字）- ピンク系
  emptyBg: "#FFF0F5",
  emptyBorder: "#D8A0B0",
  // 候補ハイライト
  highlightYellow: "#FFFACC",
};

// 重めだが彩度高めのカラフルな色（各ピース用）
const PIECE_COLORS = [
  "#A0522D", // シエナブラウン
  "#5B8A5B", // フォレストグリーン
  "#B85C5C", // テラコッタレッド
  "#4682B4", // スチールブルー
  "#DAA520", // ゴールデンロッド
  "#8B5A9B", // プラムパープル
  "#2E8B8B", // ティールグリーン
  "#CD853F", // ペルー
];

const board = {
  rows: 5,
  cols: 11,
};

const solverOption = {
  allowReverse: true,
  allowRotate: true,
};

// 濁点・半濁点を取り除き、小書き文字を大きくする
const normalizeChar = (char: string): string => {
  // 濁点・半濁点の変換マップ
  const dakutenMap: { [key: string]: string } = {
    "が": "か", "ぎ": "き", "ぐ": "く", "げ": "け", "ご": "こ",
    "ざ": "さ", "じ": "し", "ず": "す", "ぜ": "せ", "ぞ": "そ",
    "だ": "た", "ぢ": "ち", "づ": "つ", "で": "て", "ど": "と",
    "ば": "は", "び": "ひ", "ぶ": "ふ", "べ": "へ", "ぼ": "ほ",
    "ぱ": "は", "ぴ": "ひ", "ぷ": "ふ", "ぺ": "へ", "ぽ": "ほ",
    "ゔ": "う",
  };

  // 小書き文字の変換マップ
  const kogakiMap: { [key: string]: string } = {
    "ぁ": "あ", "ぃ": "い", "ぅ": "う", "ぇ": "え", "ぉ": "お",
    "っ": "つ",
    "ゃ": "や", "ゅ": "ゆ", "ょ": "よ",
    "ゎ": "わ",
  };

  let result = char;
  if (dakutenMap[result]) {
    result = dakutenMap[result];
  }
  if (kogakiMap[result]) {
    result = kogakiMap[result];
  }
  return result;
};

// 文字列全体を正規化
const normalizeInput = (input: string): string => {
  return [...input].map(normalizeChar).join("");
};

// 文字から座標を取得
const charToPosition = (char: string): { x: number; y: number } | null => {
  const normalized = normalizeChar(char);
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 11; x++) {
      if (GOJUON_BOARD[y][x] === normalized) {
        return { x, y };
      }
    }
  }
  return null;
};

// ボードのマップを作成（空けるマスを考慮）
const createBoardMaps = (emptyChars: string[]): boolean[] => {
  const maps: boolean[] = [];
  const emptyPositions = emptyChars
    .map(charToPosition)
    .filter((p): p is { x: number; y: number } => p !== null);

  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 11; x++) {
      const isValid = GOJUON_BOARD[y][x] !== "";
      const isEmpty = emptyPositions.some((p) => p.x === x && p.y === y);
      maps.push(isValid && !isEmpty);
    }
  }
  return maps;
};

interface GojuonProps {
  initialTarget?: string;
}

export const Gojuon = ({ initialTarget = "" }: GojuonProps) => {
  const [targetChars, setTargetChars] = useState<string>(initialTarget);
  const [inputValue, setInputValue] = useState<string>(initialTarget);
  const [solutions, setSolutions] = useState<number[][]>([]);
  const [currentSolutionIndex, setCurrentSolutionIndex] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string>("");
  const [validFifthChars, setValidFifthChars] = useState<Set<string>>(new Set());
  const [isCalculatingCandidates, setIsCalculatingCandidates] = useState(false);

  const fullWidth = window.innerWidth;
  const cellSize = Math.min((fullWidth - 40) / 12, 60);
  const boardWidth = cellSize * board.cols;
  const boardHeight = cellSize * board.rows;
  const offsetX = (fullWidth - boardWidth) / 2;
  const offsetY = 20;

  // 入力の検証
  const validateInput = (input: string): string | null => {
    const chars = [...input];
    if (chars.length !== 5) {
      return "5文字を入力してください";
    }

    const validChars = VALID_CELLS.map((c) => c.char);
    for (const char of chars) {
      if (!validChars.includes(char)) {
        return `「${char}」は五十音表にありません`;
      }
    }

    const uniqueChars = new Set(chars);
    if (uniqueChars.size !== 5) {
      return "同じ文字が含まれています";
    }

    return null;
  };

  // 解を計算
  const solve = useCallback(() => {
    const validationError = validateInput(targetChars);
    if (validationError) {
      setError(validationError);
      setSolutions([]);
      return;
    }

    setError("");
    setIsCalculating(true);
    setSolutions([]);

    setTimeout(() => {
      const chars = [...targetChars];
      const boardMaps = createBoardMaps(chars);
      const solverBoard = new Polyomino.Piece(board.cols, board.rows, boardMaps);

      const solver = new Polyomino.Solver(
        solverBoard,
        FINAL_PIECES,
        solverOption
      );

      const result: number[][] = [];
      solver.solveAsync(result).then(() => {
        setSolutions(result);
        setCurrentSolutionIndex(0);
        setIsCalculating(false);
      });
    }, 10);
  }, [targetChars]);

  // 入力変更時に自動で解く
  useEffect(() => {
    if ([...targetChars].length === 5) {
      solve();
    } else {
      // 5文字未満になったらリセット
      setSolutions([]);
      setCurrentSolutionIndex(0);
    }
  }, [targetChars, solve]);

  // 4文字入力時に5文字目の候補を計算
  useEffect(() => {
    if ([...targetChars].length !== 4) {
      setValidFifthChars(new Set());
      return;
    }

    const chars = [...targetChars];
    const validChars = VALID_CELLS.map((c) => c.char);

    // 入力済みの文字が有効かチェック
    for (const char of chars) {
      if (!validChars.includes(char)) {
        setValidFifthChars(new Set());
        return;
      }
    }

    // 重複チェック
    if (new Set(chars).size !== 4) {
      setValidFifthChars(new Set());
      return;
    }

    setIsCalculatingCandidates(true);

    // 非同期で各候補をチェック
    setTimeout(async () => {
      const candidates = new Set<string>();
      const remainingChars = validChars.filter((c) => !chars.includes(c));

      for (const fifthChar of remainingChars) {
        const testChars = [...chars, fifthChar];
        const boardMaps = createBoardMaps(testChars);
        const solverBoard = new Polyomino.Piece(board.cols, board.rows, boardMaps);

        const solver = new Polyomino.Solver(
          solverBoard,
          FINAL_PIECES,
          solverOption
        );

        const result: number[][] = [];
        // 4文字ハイライト時は1つ見つかれば十分なので早期終了
        await solver.solveAsync(result, 1);

        if (result.length > 0) {
          candidates.add(fifthChar);
        }
      }

      setValidFifthChars(candidates);
      setIsCalculatingCandidates(false);
    }, 10);
  }, [targetChars]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 入力値を正規化（濁点除去、小書き→大書き）
    const normalized = normalizeInput(value);
    setInputValue(normalized);
    if ([...normalized].length <= 5) {
      setTargetChars(normalized);
    }
  };

  const currentSolution = solutions[currentSolutionIndex] || null;

  // 空きマス（ターゲット文字）の位置
  const emptyPositions = useMemo(() => {
    return [...targetChars]
      .map(charToPosition)
      .filter((p): p is { x: number; y: number } => p !== null);
  }, [targetChars]);

  return (
    <div style={{ background: COLORS.background, minHeight: "100vh", padding: "20px" }}>
      {/* 五十音表（上） */}
      <Stage width={fullWidth} height={boardHeight + 60}>
        <Layer>
          {/* ボード背景 */}
          <Rect
            x={offsetX}
            y={offsetY}
            width={boardWidth}
            height={boardHeight}
            fill={COLORS.boardBg}
            stroke={COLORS.gridLine}
            strokeWidth={1}
          />

          {/* グリッド線 */}
          {[...Array(board.cols + 1)].map((_, i) => (
            <Line
              key={`v${i}`}
              points={[
                offsetX + cellSize * i,
                offsetY,
                offsetX + cellSize * i,
                offsetY + boardHeight,
              ]}
              stroke={COLORS.gridLine}
              strokeWidth={1}
            />
          ))}
          {[...Array(board.rows + 1)].map((_, i) => (
            <Line
              key={`h${i}`}
              points={[
                offsetX,
                offsetY + cellSize * i,
                offsetX + boardWidth,
                offsetY + cellSize * i,
              ]}
              stroke={COLORS.gridLine}
              strokeWidth={1}
            />
          ))}

          {/* 解答のピース表示 */}
          {currentSolution &&
            currentSolution.map((pieceIndex, i) => {
              const x = i % board.cols;
              const y = Math.floor(i / board.cols);
              if (pieceIndex < 0 || GOJUON_BOARD[y][x] === "") return null;
              return (
                <Rect
                  key={`piece${i}`}
                  x={offsetX + x * cellSize + 1}
                  y={offsetY + y * cellSize + 1}
                  width={cellSize - 2}
                  height={cellSize - 2}
                  fill={PIECE_COLORS[pieceIndex % PIECE_COLORS.length]}
                />
              );
            })}

          {/* ピース境界線 */}
          {currentSolution &&
            currentSolution.map((pieceIndex, i) => {
              const x = i % board.cols;
              const y = Math.floor(i / board.cols);
              const lines: JSX.Element[] = [];

              // 右隣と異なる場合
              if (
                x < board.cols - 1 &&
                currentSolution[i + 1] !== pieceIndex &&
                pieceIndex >= 0
              ) {
                lines.push(
                  <Line
                    key={`border-r${i}`}
                    points={[
                      offsetX + (x + 1) * cellSize,
                      offsetY + y * cellSize,
                      offsetX + (x + 1) * cellSize,
                      offsetY + (y + 1) * cellSize,
                    ]}
                    stroke="#222"
                    strokeWidth={4}
                  />
                );
              }

              // 下隣と異なる場合
              if (
                y < board.rows - 1 &&
                currentSolution[i + board.cols] !== pieceIndex &&
                pieceIndex >= 0
              ) {
                lines.push(
                  <Line
                    key={`border-b${i}`}
                    points={[
                      offsetX + x * cellSize,
                      offsetY + (y + 1) * cellSize,
                      offsetX + (x + 1) * cellSize,
                      offsetY + (y + 1) * cellSize,
                    ]}
                    stroke="#222"
                    strokeWidth={4}
                  />
                );
              }

              return lines;
            })}

          {/* 五十音表示 */}
          {GOJUON_BOARD.map((row, y) =>
            row.map((char, x) => {
              if (char === "") return null;
              const isEmpty = emptyPositions.some(
                (p) => p.x === x && p.y === y
              );
              const isCandidate = validFifthChars.has(char);
              return (
                <Group key={`char${x}-${y}`}>
                  {isEmpty && (
                    <Rect
                      x={offsetX + x * cellSize}
                      y={offsetY + y * cellSize}
                      width={cellSize}
                      height={cellSize}
                      fill={COLORS.emptyBg}
                      stroke={COLORS.emptyBorder}
                      strokeWidth={2}
                    />
                  )}
                  {isCandidate && !isEmpty && (
                    <Rect
                      x={offsetX + x * cellSize + 2}
                      y={offsetY + y * cellSize + 2}
                      width={cellSize - 4}
                      height={cellSize - 4}
                      fill={COLORS.highlightYellow}
                      opacity={0.9}
                    />
                  )}
                  <Text
                    x={offsetX + x * cellSize}
                    y={offsetY + y * cellSize}
                    width={cellSize}
                    height={cellSize}
                    text={char}
                    fontSize={cellSize * 0.5}
                    fontFamily="sans-serif"
                    fill={COLORS.ink}
                    align="center"
                    verticalAlign="middle"
                  />
                </Group>
              );
            })
          )}

          {/* 無効セル（空白）をグレーアウト */}
          {GOJUON_BOARD.map((row, y) =>
            row.map((char, x) => {
              if (char !== "") return null;
              return (
                <Rect
                  key={`invalid${x}-${y}`}
                  x={offsetX + x * cellSize}
                  y={offsetY + y * cellSize}
                  width={cellSize}
                  height={cellSize}
                  fill={COLORS.blackCell}
                />
              );
            })
          )}
        </Layer>
      </Stage>

      {/* 入力部分（下） */}
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <div style={{ marginBottom: "15px" }}>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="ひらがな5文字"
            style={{
              fontSize: "24px",
              padding: "10px 20px",
              width: "200px",
              textAlign: "center",
              border: `2px solid ${COLORS.boardBorder}`,
              borderRadius: "8px",
              background: COLORS.emptyBg,
            }}
          />
        </div>

        {error && (
          <p style={{ color: "#A05050", margin: "10px 0" }}>{error}</p>
        )}

        {isCalculating && (
          <p style={{ color: COLORS.ink }}>計算中...</p>
        )}

        {isCalculatingCandidates && (
          <p style={{ color: COLORS.ink }}>候補を検索中...</p>
        )}

        {!isCalculatingCandidates && [...targetChars].length === 4 && validFifthChars.size > 0 && (
          <p style={{ color: COLORS.ink }}>
            5文字目の候補: {validFifthChars.size}文字
          </p>
        )}

        {!isCalculatingCandidates && [...targetChars].length === 4 && validFifthChars.size === 0 && (
          <p style={{ color: "#A08050" }}>5文字目の候補がありません</p>
        )}

        {!isCalculating && solutions.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <p style={{ color: "#509050" }}>
              {solutions.length}個の解が見つかりました
            </p>
            {solutions.length > 1 && (
              <div>
                <button
                  onClick={() =>
                    setCurrentSolutionIndex((i) =>
                      i > 0 ? i - 1 : solutions.length - 1
                    )
                  }
                  style={{
                    margin: "0 10px",
                    padding: "5px 15px",
                    background: COLORS.emptyBg,
                    border: `1px solid ${COLORS.boardBorder}`,
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  前へ
                </button>
                <span style={{ color: COLORS.ink }}>
                  {currentSolutionIndex + 1} / {solutions.length}
                </span>
                <button
                  onClick={() =>
                    setCurrentSolutionIndex((i) =>
                      i < solutions.length - 1 ? i + 1 : 0
                    )
                  }
                  style={{
                    margin: "0 10px",
                    padding: "5px 15px",
                    background: COLORS.emptyBg,
                    border: `1px solid ${COLORS.boardBorder}`,
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  次へ
                </button>
              </div>
            )}
          </div>
        )}

        {!isCalculating && [...targetChars].length === 5 && solutions.length === 0 && !error && (
          <p style={{ color: "#A08050" }}>解が見つかりませんでした</p>
        )}
      </div>
    </div>
  );
};
